import { METRICS_LABELS } from "@/constants/constants";

type MetricValue = keyof typeof METRICS_LABELS;

interface ColumnOption {
  label: string;
  value: MetricValue;
}

interface ConditionRule {
  field: "newVsReturning" | "dashboardMode" | "isLink";
  value: string | boolean;
  label: string;
}

interface MetricRule {
  conditions: ConditionRule[];
  defaultLabel: string;
}

interface ContextType {
  newVsReturning?: "new" | "returning" | "total";
  dashboardMode?: "general" | string;
  isLink?: boolean;
}

type MetricRules = {
  [key in MetricValue]?: MetricRule;
};

type Metrics = {
  [key in MetricValue]: ColumnOption;
};

interface DynamicMetricResult {
  label: string;
  value: MetricValue;
}

// Configuration for dynamic column naming rules
const COLUMN_NAMING_RULES: MetricRules = {
  return_on_ad_spend: {
    conditions: [
      {
        field: "newVsReturning",
        value: "new",
        label: "Acquisition ROAS",
      },
    ],
    defaultLabel: "ROAS",
  },
  customer_revenue: {
    conditions: [
      {
        field: "dashboardMode",
        value: "general",
        label: "Conv. Value",
      },
    ],
    defaultLabel: "Revenue",
  },
  click_through_rate: {
    conditions: [
      {
        field: "isLink",
        value: true,
        label: "CTR (link)",
      },
    ],
    defaultLabel: "CTR",
  },
};

// Function to get the dynamic label based on conditions
const getDynamicMetric = (
  metric: MetricValue,
  context: ContextType | string
): DynamicMetricResult | null => {
  // Handle simple context value for newVsReturning
  const normalizedContext: ContextType =
    typeof context === "string"
      ? { newVsReturning: context as "new" | "returning" }
      : context;

  const rules = COLUMN_NAMING_RULES[metric];
  if (!rules) return null;

  const matchingCondition = rules.conditions.find(
    (condition) => normalizedContext[condition.field] === condition.value
  );

  if (matchingCondition) {
    return {
      label: matchingCondition.label,
      value: metric,
    };
  }

  return {
    label: rules.defaultLabel,
    value: metric,
  };
};

// Main function to generate column options
const generateColumnOptions = (context: ContextType | string = {}): Metrics => {
  const metrics = {} as Metrics;

  (Object.keys(METRICS_LABELS) as MetricValue[]).forEach((metric) => {
    const dynamicMetric = getDynamicMetric(metric, context);

    metrics[metric] = {
      value: dynamicMetric?.value || metric,
      label: dynamicMetric?.label || METRICS_LABELS[metric],
    };
  });

  return metrics;
};

export default generateColumnOptions;
